import { apiClient, ApiError } from './apiClient';

async function wizardRequest<T>(operation: 'preview' | 'publish', payload: unknown): Promise<T> {
  const endpoint = `/api/schedules/wizard/${operation}`;
  try {
    return await apiClient.post<T>(endpoint, payload);
  } catch (failure) {
    if (!(failure instanceof ApiError)) throw failure;
    const action = operation === 'preview' ? 'Validate schedule coverage' : 'Publish schedule';
    let guidance: string;
    if (failure.status === 404 && (!failure.details || Object.keys(failure.details).length === 0)) {
      guidance = `${action} could not run because the connected API did not provide POST ${endpoint} (HTTP 404). This is an API availability/version or routing problem, not a missing value in your roster. Ask your administrator to start or deploy the matching scheduler-wizard API and check the portal API address, then retry. Your draft is retained.`;
    } else if (failure.status === 401) {
      guidance = `${action} requires a valid login for this tenant. Sign in again to the same tenant before retrying.`;
    } else if (failure.status === 403) {
      guidance = `${action} was denied. Ask a tenant administrator to verify your ManageSchedules permission and tenant access.`;
    } else if (failure.status === 0) {
      guidance = `${action} could not reach the connected API. Check that the API is running and the portal API address/network connection is correct, then retry. Your draft is retained.`;
    } else {
      guidance = `${action} failed (HTTP ${failure.status}): ${failure.message}. ${operation === 'preview'
        ? 'Check the reported prerequisite or coverage finding; if the service is unavailable, ask your administrator to check the API and database, then retry. Your draft is retained.'
        : 'Follow the publication recovery instructions below; do not start a second schedule to recover a lost result.'}`;
    }
    throw new ApiError(failure.status, failure.code, guidance, failure.details);
  }
}

export type DutyRole = 'Primary' | 'Escalation' | 'Backup';
export type DstAmbiguity = 'Earlier' | 'Later';
export interface LocalDateTime { date: string; time: string; ambiguityResolution?: DstAmbiguity | null }
export interface WizardBand {
  id: string; startDay: string; startTime: string; endDay: string; endTime: string; endDayOffset: number;
  startAmbiguityResolution?: DstAmbiguity | null; endAmbiguityResolution?: DstAmbiguity | null;
}
export interface CoverageRequirement extends WizardBand {
  role: DutyRole; required: boolean; requiredPositions: number; allowParallel: boolean;
}
export interface WizardLane {
  id: string; role: DutyRole; groupId: string; assignmentCadence: 'Cycle' | 'Daily' | 'Custom';
  assignmentDurationMinutes?: number | null; rosterUserIds: string[]; initialUserId: string;
  bands: WizardBand[]; ownershipAssociationId?: string | null;
}
export interface WizardDraft {
  draftId: string; name: string; description: string; timeZoneId: string;
  managingGroupId?: string | null;
  escalationLinkedToPrimary?: boolean;
  effectiveStartLocal: LocalDateTime; effectiveEndLocal: LocalDateTime | null;
  cycle: {
    basis: 'CalendarWeeks'; weeks: number; anchorWeekday: string; anchorTime: string; phaseDate: string;
    anchorAmbiguityResolution?: DstAmbiguity | null; invalidLocalTimeResolution: 'Reject' | 'ShiftForward';
  };
  coverageRequirements: CoverageRequirement[]; lanes: WizardLane[];
  ownershipAssociations: { id: string; sourceLaneId: string; followerLaneIds: string[] }[];
  exclusions: (WizardBand & { role: DutyRole; reason: string; confirmed: boolean })[];
  decisions: {
    escalation: 'NotConfigured' | 'Configure' | 'NotRequired';
    additionalRequirements: 'NotConfigured' | 'None' | 'Configured';
  };
}
export interface WizardFinding {
  code: string; message: string; step: number; laneId?: string | null; bandId?: string | null;
  role?: string | null; startUtc?: string | null; endUtc?: string | null;
  startLocal?: string | null; endLocal?: string | null; draftId?: string | null;
}
export interface WizardPreview {
  previewId: string; normalizedDraft: WizardDraft;
  revisions: { membership: string; configuration: string };
  blockingErrors: WizardFinding[]; warnings: WizardFinding[]; findings: WizardFinding[];
  timeline: {
    startUtc: string; endUtc: string; startLocal: string; endLocal: string; laneId?: string | null;
    bandId?: string | null; role: string; groupId: string | null; assigneeId: string | null;
    assigneeName: string | null; kind: string; isRequired: boolean;
  }[];
  previewRange: { startUtc: string; endUtc: string; cycles: number; fullRosterTraversal: boolean; isFinite: boolean };
}
export interface WizardPublished {
  scheduleId: string; escalationScheduleId?: string | null; identityMap: Record<string, string>; committed: boolean;
  projectionStatus: 'Ready' | 'RecoveryRequired'; recoveryId: string | null; duplicate: boolean;
}
export const schedulerWizardApi = {
  preview: (draft: WizardDraft, validationMode: 'PrimaryOnly' | 'Complete' = 'Complete') =>
    wizardRequest<WizardPreview>('preview', { draft, validationMode }),
  publish: (draft: WizardDraft, previewId: string, requestId: string) =>
    wizardRequest<WizardPublished>('publish', { draft, previewId, requestId }),
};
